import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getTierTopicConfig,
  getTierTopicList,
  setTierTopicConfig,
} from '@/pages/opinion/services/summary';
import { dealwithTopicList } from '../utils';
import { arrayToObj } from '@op/utils';
import { useTopicTrackingLoading } from '@op/models/loading/useTopicTrackingLoading';
import { sortable } from 'tea-component/lib/table/addons';
import { useUpdateEffect } from 'ahooks';
import { topicTrackingSortItemMap } from '@op/components/TableCommonHook/columns/topicTracking';
import { PageInfoType } from '@op/types/common';

/**
 * 该hook只获取快捷选项
 * @param gameInfo
 * @returns
 */
export function useTopicSelectConfig({ gameInfo }: any) {
  const [userQuickPickSelect, setUserQuickPickSelect] = useState<{
    parent: Array<string>;
    children: Array<string>;
  }>({ parent: [], children: [] });

  const requestServerTopic = async () => {
    let topicMap: { parent: Array<string>; children: Array<string> } = {
      parent: [],
      children: [],
    };
    try {
      const topicInfo = await getTierTopicConfig(gameInfo);

      if (topicInfo) {
        topicMap = JSON.parse(
          topicInfo?.topic_conf || '{ parent: [], children: [] }',
        );
      }
    } catch (err) {
      console.error('获取快捷选项失败:', err);
    }
    setUserQuickPickSelect(topicMap);
  };

  const [settingLoading, setSettingLoading] = useState(false);
  const setUserTopicConfig = async (
    parent: Array<string>,
    children: Array<string>,
  ) => {
    setSettingLoading(true);
    try {
      await setTierTopicConfig({
        edition_unified_id: gameInfo.edition_unified_id,
        topic_conf: JSON.stringify({
          parent: parent || [],
          children: children || [],
        }),
      });
      await requestServerTopic();
    } catch (err) {
      console.error('设置快捷选项失败:', err);
    } finally {
      setSettingLoading(false);
    }
  };

  // 拿快捷选项
  useEffect(() => {
    if (gameInfo.edition_unified_id) {
      requestServerTopic();
    }
  }, [gameInfo]);

  return {
    userQuickPickSelect,
    setUserTopicConfig,
    settingLoading,
  };
}

/**
 * 该hook只组装成快捷选项，组装成topic select筛选器的值
 * @param topicOneLevel       一级话题
 * @param topicTwoLevel       二级话题
 * @param userQuickPickSelect 用户自定义的快捷选项
 * @param tableTopicList      如果没有自定义选项，那么就默认展示的topic
 * @param topicTree           翻译快捷选项
 * @returns
 */
export function useQuickPickOptions({
  topicOneLevel,
  topicTwoLevel,
  userQuickPickSelect,
  tableTopicList,
  topicTree,
  setTopic2Select,
}: any) {
  const [quickPickSelect, setQuickPickSelect] = useState<
    {
      value: string;
      isNotHaveDisable: boolean;
    }[]
  >([]);

  useEffect(() => {
    let parent: Array<{ value: string; isNotHaveDisable: boolean }> = [],
      children: Array<string> = [];
    if (userQuickPickSelect.parent.length === 0) {
      const list = tableTopicList.slice(0, 5);
      parent = [...list].map((item: { topic: string }) => ({
        value: item.topic,
        isNotHaveDisable: false,
      }));
      children = parent?.reduce(
        (pre: any, { value: cur }) => [
          ...pre,
          ...(topicTree[cur?.toLocaleLowerCase()] || []),
        ],
        [],
      );
    } else {
      parent = userQuickPickSelect.parent.map((item) => ({
        value: item,
        isNotHaveDisable: !topicOneLevel[item],
      }));
      parent.sort((a, b) => a.value.localeCompare(b.value));
      children = userQuickPickSelect.children;
    }

    children = children.map((key: string) => {
      let newKey = key.toLocaleLowerCase();
      return topicTwoLevel?.[newKey]?.origin || key;
    });

    setQuickPickSelect(parent);
    setTopic2Select(children);
  }, [userQuickPickSelect, tableTopicList]);

  return quickPickSelect;
}

/**
 * 获取一级话题展示
 * @param filter
 * @returns
 */
export function useTopicLevelOneList({ filter, topicLevel }: any) {
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(false);

  const requestServerData = () => {
    setLoading(true);
    setTableData([]);
    getTierTopicList({
      ...filter,
      topic_level: 'L1',
      metrics: ['mentions', 'sentiment'],
      types: ['total', 'link'],
      page: 1,
      page_size: 1000,
      sort_item: 'mentions',
      sort_model: 'desc',
    })
      .then((data: { list: any[]; total: number }) => {
        const { tableData, total } = dealwithTopicList(data, filter);
        setTableData(tableData);
        setTotal(total);
      })
      .catch(() => {
        setTableData([]);
        setTotal(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 拿快捷选项
  useEffect(() => {
    if (filter.edition_unified_id) {
      requestServerData();
    }
  }, [filter, topicLevel]);

  return { total, tableData, loading };
}

/**
 * 折线图板块获取二级话题展示
 * @param gameInfo
 * @param filter
 * @returns
 */
export function useTopicLevelTwoList({
  filter,
  topic,
  index,
}: {
  filter: any;
  topic: Array<string>;
  index?: number;
}) {
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState<Array<any>>([]);

  const { canRequest, finishLoading, needLoading } = useTopicTrackingLoading({
    index,
    key: 'topicTrackingList',
  });

  const requestServerData = () => {
    let topicMap = {};
    if (topic) topicMap = { topic };
    getTierTopicList({
      ...filter,
      ...topicMap,
      topic_level: 'L2',
      metrics: ['mentions', 'sentiment'],
      types: ['total', 'link', 'week_link'],
      page: 1,
      page_size: 1000,
      sort_item: 'mentions',
      sort_model: 'desc',
    })
      .then((data: { list: any[]; total: number }) => {
        const { tableData, total } = dealwithTopicList(data, filter);
        setTableData(tableData);
        setTotal(total);
      })
      .catch(() => {
        setTableData([]);
        setTotal(0);
      })
      .finally(() => {
        finishLoading('topicTrackingList', typeof index === 'number');
      });
  };

  useEffect(() => {
    if (filter.edition_unified_id && canRequest) {
      requestServerData();
    }
  }, [canRequest]);

  const tableRecodeMap = useMemo(() => {
    return arrayToObj(tableData, 'topic');
  }, [tableData]);

  return { total, tableData, loading: needLoading, tableRecodeMap };
}

/**
 * Nikke表格部分获取二级话题展示
 * @param gameInfo
 * @param filter
 * @returns
 */
export function useNikkeTableTopicLevelTwoList({
  filter,
  defaultPageInfo = {
    page: 1,
    page_size: 20,
  },
  dealwithTableData = dealwithTopicList,
  otherMetrics = ['views', 'neutral_mentions'],
  saveDefaultData,
  clearAllSelectInfo,
}: {
  filter: any;
  defaultPageInfo?: PageInfoType;
  dealwithTableData?: (
    data: any,
    filter: any,
  ) => { total: number; tableData: Array<any> };
  otherMetrics?: Array<string>;
  saveDefaultData?: (list: Array<any>) => void;
  clearAllSelectInfo?: () => void;
}) {
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [sort, setSort] = useState<{ by: string; order: 'desc' | 'asc' }>({
    by: 'mentions',
    order: 'desc',
  });

  function getRequestParams() {
    const sortMap = {
      ...topicTrackingSortItemMap,
      mentions_qoq: 'mentions',
      mentions_ratio_qoq: 'mentions',
      negative_mentions_ratio_qoq: 'negative_mentions',
      positive_mentions_ratio_qoq: 'positive_mentions',
      neutral_mentions_ratio_qoq: 'neutral_mentions',
      engagement_qoq: 'engagement',
      avg_sentiment_qoq: 'avg_sentiment',
      engagement_ratio: 'engagement',
      engagement_ratio_qoq: 'engagement',
    };

    return {
      ...filter,
      ...pageInfo,
      topic_level: 'L2',
      metrics: [
        'mentions',
        'avg_sentiment',
        'engagement',
        'negative_mentions',
        'positive_mentions',
        ...otherMetrics,
      ],
      types: ['total', 'link'],
      sort_item: sortMap[sort.by] || sort.by,
      sort_model: sort.order,
    };
  }

  const requestServerData = () => {
    setLoading(true);

    getTierTopicList(getRequestParams())
      .then((data: { list: any[]; total: number }) => {
        const { tableData, total } = dealwithTableData(data, filter);
        setTableData(tableData);
        setTotal(total);
        saveDefaultData?.(tableData);
      })
      .catch(() => {
        setTableData([]);
        setTotal(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const canRequest = useRef(true);

  useUpdateEffect(() => {
    if (canRequest.current) {
      requestServerData();
    }

    if (pageInfo.page_size === 1000) {
      canRequest.current = false;
    }
  }, [pageInfo]);

  useEffect(() => {
    clearAllSelectInfo?.();
    requestServerData();
  }, [filter]);

  useUpdateEffect(() => {
    if (canRequest.current) {
      clearAllSelectInfo?.();
      requestServerData();
    }
  }, [sort]);

  const records = useMemo(() => {
    const list = tableData?.sort(sortable.comparer([sort])) || [];

    if (!canRequest.current) {
      clearAllSelectInfo?.();
      saveDefaultData?.(list);
    }
    return list;
  }, [tableData, sort]);

  return {
    total,
    records,
    loading,
    setPageInfo,
    pageInfo,
    sort,
    setSort,
    getRequestParams,
  };
}

/**
 * 表格部分获取二级话题展示
 * @param gameInfo
 * @param filter
 * @returns
 */
export function useTableTopicLevelTwoList({
  filter,
  topic,
  defaultPageInfo = {
    page: 1,
    page_size: 10,
  },
  dealwithTableData = dealwithTopicList,
  otherMetrics = [],
  topN,
  selectTopN,
}: {
  filter: any;
  topic?: Array<string>;
  defaultPageInfo?: PageInfoType;
  dealwithTableData?: (
    data: any,
    filter: any,
  ) => { total: number; tableData: Array<any> };
  otherMetrics?: Array<string>;
  topN: number;
  selectTopN: boolean;
}) {
  const [total, setTotal] = useState(0);
  const [tableData, setTableData] = useState<Array<any>>([]);
  const [loading, setLoading] = useState(true);
  const [pageInfo, setPageInfo] = useState(defaultPageInfo);
  const [sort, setSort] = useState<{ by: string; order: 'desc' | 'asc' }>({
    by: 'avg_sentiment',
    order: 'asc',
  });

  function getRequestParams() {
    const sortMap = {
      ...topicTrackingSortItemMap,
      mentions_qoq: 'mentions',
      mentions_ratio_qoq: 'mentions',
      negative_mentions_ratio_qoq: 'negative_mentions',
      positive_mentions_ratio_qoq: 'positive_mentions',
      neutral_mentions_ratio_qoq: 'neutral_mentions',
      engagement_qoq: 'engagement',
      avg_sentiment_qoq: 'avg_sentiment',
      engagement_ratio: 'engagement',
      engagement_ratio_qoq: 'engagement',
    };
    let topicMap = {};
    if (topic) topicMap = { topic: topic };

    let localSort = selectTopN ? { by: 'mentions', order: 'desc' } : sort;
    let localPageInfo =
      selectTopN && pageInfo.page_size !== 1000
        ? { page: 1, page_size: topN }
        : pageInfo;

    return {
      ...filter,
      ...topicMap,
      ...localPageInfo,
      topic_level: 'L2',
      metrics: [
        'mentions',
        'avg_sentiment',
        'engagement',
        'negative_mentions',
        'positive_mentions',
        ...otherMetrics,
      ],
      types: ['total', 'link'],
      sort_item: sortMap[localSort.by] || localSort.by,
      sort_model: localSort.order,
    };
  }

  const requestServerData = () => {
    setLoading(true);

    getTierTopicList(getRequestParams())
      .then((data: { list: any[]; total: number }) => {
        const { tableData, total } = dealwithTableData(data, filter);
        setTableData(tableData);
        setTotal(total);
      })
      .catch(() => {
        setTableData([]);
        setTotal(0);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const canRequest = useRef(true);

  useUpdateEffect(() => {
    if (canRequest.current && (!selectTopN || pageInfo.page_size === 1000)) {
      requestServerData();
    }

    if (pageInfo.page_size === 1000) {
      canRequest.current = false;
    }
  }, [pageInfo]);

  useUpdateEffect(() => {
    if (canRequest.current && !selectTopN) {
      requestServerData();
    }
  }, [sort]);

  useEffect(() => {
    requestServerData();
  }, [filter]);

  const records = useMemo(() => {
    const list = tableData?.sort(sortable.comparer([sort])) || [];

    return list;
  }, [tableData, sort]);

  return {
    total,
    records,
    loading,
    setPageInfo,
    pageInfo,
    sort,
    setSort,
    getRequestParams,
    requestServerData,
  };
}
